import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Redirect, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import { reaction } from 'mobx';

import { Loading } from 'core';
import { useStore } from 'core/store';
import { AuthContext } from 'core/components/auth';
import { Card } from 'core/components/card';
import EditableCard from 'core/components/editable-card';
import Section from 'core/components/section';
import { useToast } from 'core/components/toast';
import AccountDetailsForm from './components/account-details-form';
import AccountDetailsDisplay from './components/account-details-display';
import useStyles from './account-details.styles';

// `disableAccountRouteEffects` turns off the behaviors that only make sense when this card
// IS the account landing page route: the redirect-to-search guard and opening in edit mode
// from `location.state.openAccountCard`. The policy sidebar embeds this card (ticket #26540),
// where the policy route owns navigation, so it passes this `true`. Editing via the pencil is
// unaffected — this only changes the card's initial mode.
const AccountDetail = ({ disableAccountRouteEffects = false }) => {
  const { account } = useStore();
  const { canEditPolicies } = useContext(AuthContext);
  const location = useLocation();
  const [editing, setEditing] = useState(!disableAccountRouteEffects && Boolean(location.state?.openAccountCard));
  const toast = useToast();
  const classes = useStyles();

  const onUpdateSuccess = useCallback(() => {
    setEditing(false);
    toast.notify({
      type: 'success',
      message: 'Account updated successfully'
    });
  }, [toast, setEditing]);

  const onUpdateFailed = useCallback(
    (errorMessage) => {
      toast.notify({
        type: 'error',
        message: `Please fix the required form items to update the account details. ${errorMessage}`
      });
    },
    [toast]
  );

  const CardComponent = useMemo(() => {
    if (canEditPolicies) {
      return EditableCard;
    }
    return Card;
  }, [canEditPolicies]);

  // Redirect to customer search once loading finishes with no account resolved. Registered
  // in an effect so the reaction is disposed on unmount instead of leaking a fresh reaction
  // on every render. accountLoading starts false, so we wait for the true→false transition
  // before treating a missing id as "no such account" — a synchronous check would redirect
  // before the fetch begins.
  const [noAccount, setNoAccount] = useState(false);
  useEffect(() => {
    if (disableAccountRouteEffects) {
      return undefined;
    }
    return reaction(
      () => account.accountLoading,
      (loading) => {
        if (!loading && !account.id) {
          setNoAccount(true);
        }
      }
    );
  }, [account, disableAccountRouteEffects]);

  if (noAccount) {
    return <Redirect to="/search/customers" />;
  }

  return (
    <Section title="Account Details" id="account-details">
      {account.accountLoading || !account.id ? (
        <Loading />
      ) : (
        <CardComponent className={classes.accountCard} editing={editing} setEditing={setEditing}>
          {editing ? (
            <AccountDetailsForm onSuccess={onUpdateSuccess} onError={onUpdateFailed} />
          ) : (
            <AccountDetailsDisplay />
          )}
        </CardComponent>
      )}
    </Section>
  );
};

AccountDetail.propTypes = {
  disableAccountRouteEffects: PropTypes.bool
};

export default observer(AccountDetail);
